import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_3,
  KPI_STRIP_HEIGHT,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  RADIUS_MD,
  SHADOW_XS,
  TOPBAR_HEIGHT,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaProductGallery — beta-styled product image gallery (left column of the
 * Product Details tab). Matches the mockup's `.gallery / .gallery-main /
 * .gallery-thumbs` block:
 *
 *   ┌──────────────────────┐
 *   │ ⟨    [ main image ]  ⟩
 *   ├──────────────────────┤
 *   │ [▢][▢][▢][▢]         │ ← up to 4 thumbnails
 *   └──────────────────────┘
 *
 * Sticky on tall pages so the gallery stays in view as the right column
 * scrolls.  Falls back to a placeholder swatch when the product has no images.
 */
function BetaProductGallery({ images = [] }) {
  const validImages = useMemo(
    () => (images || []).filter(img => img && img.url && !img.url.includes('placeholder-img')),
    [images],
  );
  const [activeIndex, setActiveIndex] = useState(0);

  // Reset selection when the image list changes (different product loaded).
  useEffect(() => {
    setActiveIndex(0);
  }, [validImages.length]);

  const hasImages = validImages.length > 0;
  const selected = hasImages ? validImages[activeIndex] : null;
  const showNav = validImages.length > 1;

  const go = delta => {
    setActiveIndex(idx => {
      const next = idx + delta;
      if (next < 0) return validImages.length - 1;
      if (next >= validImages.length) return 0;
      return next;
    });
  };

  return (
    <aside className={css(styles.gallery)}>
      <div className={css(styles.main)}>
        {showNav && (
          <button
            type="button"
            className={css(styles.nav, styles.navLeft)}
            onClick={() => go(-1)}
            aria-label="Previous image"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        {hasImages ? (
          <img className={css(styles.mainImg)} src={selected.url} alt="" />
        ) : (
          <div className={css(styles.placeholder)} aria-label="No image">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="11" r="1.6" />
              <polyline points="21 17 16 12 5 21" />
            </svg>
          </div>
        )}
        {showNav && (
          <button
            type="button"
            className={css(styles.nav, styles.navRight)}
            onClick={() => go(1)}
            aria-label="Next image"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {showNav && (
        <div className={css(styles.thumbs)}>
          {validImages.slice(0, 4).map((img, idx) => (
            <button
              key={img.id || img.url || idx}
              type="button"
              className={css(styles.thumb, idx === activeIndex && styles.thumbActive)}
              onClick={() => setActiveIndex(idx)}
              aria-label={`Image ${idx + 1}`}
            >
              <img className={css(styles.thumbImg)} src={img.url} alt="" />
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}

BetaProductGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
    }),
  ),
};

BetaProductGallery.defaultProps = {
  images: [],
};

// Sticky offset = topbar + KPI strip + a comfortable gap so the gallery card
// doesn't kiss the bottom of the KPI strip when stuck.
const STICKY_TOP = TOPBAR_HEIGHT + KPI_STRIP_HEIGHT + 20;

const styles = StyleSheet.create({
  gallery: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    position: 'sticky',
    top: STICKY_TOP,
  },

  main: {
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_MD,
    aspectRatio: '1 / 1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },

  mainImg: {
    maxWidth: '74%',
    maxHeight: '74%',
    objectFit: 'contain',
  },

  placeholder: {
    color: FG_3,
    width: '40%',
    height: '40%',
  },

  nav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 36,
    height: 36,
    borderRadius: 9999,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: FG_3,
    boxShadow: SHADOW_XS,
    ':hover': {
      backgroundColor: BG_2,
    },
  },

  navLeft: { left: 10 },
  navRight: { right: 10 },

  thumbs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8,
  },

  thumb: {
    aspectRatio: '1 / 1',
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 8,
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
  },

  thumbActive: {
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}`,
  },

  thumbImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
});

export default BetaProductGallery;
